/**
 * Calculate extra charge for a specific banquet course item.
 * Matches by item ID (or name fallback), item type, and variant ID.
 *
 * @param {Array} extra_items - The banquet's extra_items array from API
 * @param {Object} item - The course item being evaluated
 * @returns {number} - Extra charge amount (0 if not an extra-priced item)
 */
export function calculateBanquetExtraPrice(extra_items = [], item) {
    if (!Array.isArray(extra_items) || !item) return 0;

    const targetItemId = item?.item_id ?? item?.menu_item?.id;
    const targetItemType = item?.item_type;
    const targetVariantId = item?.variant?.id ?? item?.variant_id ?? null;
    const targetName = item?.menu_item?.name ?? item?.name ?? item?.display_name;

    const matched = extra_items.find((extra_item) => {
        const extraItemId = extra_item?.item_id ?? extra_item?.menu_item?.id;
        const extraItemType = extra_item?.item_type;
        const extraVariantId = extra_item?.variant?.id ?? extra_item?.variant_id ?? null;
        const extraName = extra_item?.menu_item?.name ?? extra_item?.name ?? extra_item?.display_name;

        // Compare by ID if both exist, otherwise fallback to name
        if (targetItemId != null && extraItemId != null) {
            if (String(targetItemId) !== String(extraItemId)) return false;
        } else if (targetName && extraName) {
            if (targetName.trim().toLowerCase() !== extraName.trim().toLowerCase()) return false;
        } else {
            return false;
        }

        // Compare item_type if present on both
        if (targetItemType && extraItemType && targetItemType !== extraItemType) {
            return false;
        }

        // Compare variant (null/undefined matches null/undefined)
        const normTargetVariant = targetVariantId == null ? "" : String(targetVariantId);
        const normExtraVariant = extraVariantId == null ? "" : String(extraVariantId);
        if (normTargetVariant !== normExtraVariant) {
            return false;
        }

        return true;
    });

    return Number(matched?.extra_charge || 0);
}

/**
 * Calculate total banquet price including base price and all selected course item extra charges.
 *
 * @param {Object} menuItem - The banquet menu item (contains price and extra_items)
 * @param {Array} selectedCourseItem - Selected courses with their selected course_items
 * @returns {number} - Total price with extra charges added
 */
export function calculateBanquetPrice(menuItem, selectedCourseItem = []) {
    if (!menuItem) return 0;

    const basePrice = Number(
        menuItem?.discount_price || menuItem?.price || menuItem?.in_price || menuItem?.out_price || 0
    );
    const extraItems = menuItem?.extra_items || [];

    let totalExtraCharge = 0;

    if (Array.isArray(selectedCourseItem)) {
        selectedCourseItem.forEach((course) => {
            const courseItems = course?.course_items || [];
            courseItems.forEach((item) => {
                const extraPrice = calculateBanquetExtraPrice(extraItems, item);
                const quantity = Number(item?.quantity) || 1;
                totalExtraCharge += extraPrice * quantity;
            });
        });
    }

    // Fallback: if no courses matched extra charges, but banquet_extra_price is present on menuItem
    if (totalExtraCharge === 0 && Number(menuItem?.banquet_extra_price) > 0) {
        totalExtraCharge = Number(menuItem.banquet_extra_price);
    }

    return Number((basePrice + totalExtraCharge).toFixed(2));
}

/**
 * Checks whether two course items represent the same underlying selection.
 * Handles course_item PK, unique_id, composite type+item_id+variant, and name+variant matching.
 */
export function isSameCourseItem(existingItem, targetItem) {
    if (!existingItem || !targetItem) return false;

    // 1. If unique client IDs match
    const uniqueA = existingItem.course_item_unique_id || existingItem.unique_id;
    const uniqueB = targetItem.course_item_unique_id || targetItem.unique_id;
    if (uniqueA && uniqueB && String(uniqueA) === String(uniqueB)) {
        return true;
    }

    // 2. If course item DB PK matches
    if (existingItem.id && targetItem.id && String(existingItem.id) === String(targetItem.id)) {
        return true;
    }

    // 3. Composite match: item_type + sub-table item_id/menu_item_id + variant
    const typeA = existingItem.item_type || "";
    const typeB = targetItem.item_type || "";
    const subItemIdA = existingItem.item_id ?? existingItem.menu_item?.id;
    const subItemIdB = targetItem.item_id ?? targetItem.menu_item?.id;
    const varA = existingItem.variant?.id ?? existingItem.variant_id ?? existingItem.variant?.name ?? "";
    const varB = targetItem.variant?.id ?? targetItem.variant_id ?? targetItem.variant?.name ?? "";

    if (typeA && typeB && typeA === typeB && subItemIdA && subItemIdB && String(subItemIdA) === String(subItemIdB)) {
        return String(varA) === String(varB);
    }

    // 4. Name + variant match fallback
    const nameA = existingItem.menu_item?.name || existingItem.display_name || existingItem.name || "";
    const nameB = targetItem.menu_item?.name || targetItem.display_name || targetItem.name || "";
    const varNameA = existingItem.variant?.name || (typeof existingItem.variant === 'string' ? existingItem.variant : "") || "";
    const varNameB = targetItem.variant?.name || (typeof targetItem.variant === 'string' ? targetItem.variant : "") || "";

    if (nameA && nameB && String(nameA).trim().toLowerCase() === String(nameB).trim().toLowerCase()) {
        return String(varNameA).trim().toLowerCase() === String(varNameB).trim().toLowerCase();
    }

    return false;
}
